import { Controller } from "@hotwired/stimulus";

// Bidirectional lazy pagination for the docked class-chat feed. Mounted
// directly on the scroll container (`#chat_feed_<id>`, `data-role="message-feed"`).
// The show endpoint (Classroom::ConversationsController#show) returns a bare,
// chronological `.chat-message` HTML fragment for both `?before=` and
// `?after=` cursors -- this controller only prepends/appends it, it never
// reverses it (D5).
//
// Cross-controller contract with chat_controller.js (D7 + G2 hardening):
// - Does not start observing/fetching until BOTH (a) a single `chat:positioned`
//   document CustomEvent has been received (dispatched once by chat_controller
//   after its initial scroll-to-anchor rAF completes on first panel open) and
//   (b) the `chat` outlet is connected. Before that the feed is hidden /
//   `scrollHeight === 0`, so paginating early would be both wrong and racy.
//   After a cross-page Turbo visit with the panel left open, chat_controller
//   announces from its own connect() -- before this controller is listening --
//   so (a) is also satisfied by the outlet's `positioned` flag.
// - Seeds every fetched message id into `chatOutlet.seenMessageIds` (as a
//   string, matching `node.dataset.messageId`) BEFORE inserting the fetched
//   nodes into the DOM. chat_controller's MutationObserver-driven
//   `handleAddedMessage` short-circuits realtime side effects (autoscroll,
//   unread badge/sound, mark-read) for ids already in that set -- so
//   historical nodes brought in by pagination never trigger them.
export default class extends Controller {
  static values = {
    url: String
  };

  static outlets = ["chat"];

  connect() {
    this.positioned = false;
    this.started = false;
    // Not reset on a reconnect: Stimulus reuses this controller for the same
    // (permanent, merely relocated) feed node, so a fetch started before a
    // cross-page Turbo visit is still in flight and still clears its own flag
    // in `finally`. Resetting here would let the restarted observers issue a
    // second request for the same cursor and insert that page twice.
    this.loadingBefore ??= false;
    this.loadingAfter ??= false;
    this.beforeExhausted = false;

    // Realtime-append contiguity (finding #1 hardening): whether the initial
    // server-rendered window already reaches the true newest message is
    // knowable synchronously from the DOM, with no fetch round trip needed.
    // Chat::Panel::Component renders `[data-role="first-unread-anchor"]`
    // ONLY for its anchor branch (`after: anchor_id - 1`, which may or may
    // not reach the newest); its other branch (no cursor -- always the
    // literal latest N via HistoryQuery) is unconditionally at the newest,
    // and renders no anchor at all. Seeding this here instead of leaving it
    // `false` until a live `loadAfter` empirically confirms an empty next
    // page closes a real race: a realtime append arriving before that
    // confirmation round trip resolves would otherwise be treated as
    // arriving into an unconfirmed (potentially non-newest) window and
    // wrongly pulled back out by `handleRealtimeAppend`, even when the
    // window was already, provably, the true newest from the start.
    this.afterExhausted = !this.element.querySelector('[data-role="first-unread-anchor"]');

    // SF2 hardening: count of realtime appends pulled back out of the DOM by
    // `handleRealtimeAppend` while `afterExhausted` was false but the arrival
    // may in fact have been contiguous (not provable cheaply -- see that
    // method). Read by chat_controller.js#countMessagesBelowViewport so the
    // jump-to-latest button still surfaces as a signal even though the
    // pulled node itself left no DOM trace. Reset once the gap is actually
    // resolved (a confirmed contiguous `loadAfter` or a `jumpToLatest`).
    this.pendingRealtimeCount = 0;

    this.boundHandlePositioned = this.handlePositioned.bind(this);
    document.addEventListener("chat:positioned", this.boundHandlePositioned);

    // Stimulus starts the outlet observer before calling connect(), so
    // `chatOutletConnected` has already run by now -- against the flags as
    // they were before the reset above. On a reconnect (cross-page Turbo
    // visit) that leaves nobody to start the observers again.
    this.maybeStart();
  }

  disconnect() {
    document.removeEventListener("chat:positioned", this.boundHandlePositioned);
    this.topObserver?.disconnect();
    this.bottomObserver?.disconnect();
  }

  chatOutletConnected() {
    this.maybeStart();
  }

  handlePositioned() {
    this.positioned = true;
    this.maybeStart();
  }

  // Both the initial-anchor-positioned signal and the outlet connection are
  // required before we start observing -- see the class docstring.
  maybeStart() {
    if (this.started || !this.hasChatOutlet) return;
    if (!this.positioned && !this.chatOutlet.positioned) return;
    this.started = true;

    this.topObserver = new IntersectionObserver((entries) => this.handleTopIntersect(entries), {
      root: this.element,
      rootMargin: "100px 0px 0px 0px"
    });
    this.bottomObserver = new IntersectionObserver((entries) => this.handleBottomIntersect(entries), {
      root: this.element,
      rootMargin: "0px 0px 100px 0px"
    });

    this.retargetObservers();
  }

  handleTopIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.loadBefore();
    });
  }

  handleBottomIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.loadAfter();
    });
  }

  // Re-targets each observer at the current oldest/newest `.chat-message` --
  // cursors are always derived from the live DOM (never a JS-held id), so a
  // `data-turbo-permanent` morph can't desync them (D6).
  retargetObservers() {
    if (this.topObserver && !this.beforeExhausted) {
      this.topObserver.disconnect();
      const oldest = this.oldestMessageElement();
      if (oldest) this.topObserver.observe(oldest);
    }

    if (this.bottomObserver && !this.afterExhausted) {
      this.bottomObserver.disconnect();
      const newest = this.newestMessageElement();
      if (newest) this.bottomObserver.observe(newest);
    }
  }

  async loadBefore() {
    if (!this.urlValue || this.loadingBefore || this.beforeExhausted) return;
    const oldest = this.oldestMessageElement();
    if (!oldest) return;

    this.loadingBefore = true;

    try {
      const response = await fetch(this.buildUrl({ before: oldest.dataset.messageId }), {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
      });

      if (!response.ok) return;

      const html = await response.text();
      if (!html.trim()) {
        this.beforeExhausted = true;
        this.topObserver?.disconnect();
        return;
      }

      this.insertBefore(html);
    } catch (error) {
      console.error("Error loading older chat messages:", error);
    } finally {
      this.loadingBefore = false;
    }
  }

  async loadAfter() {
    if (!this.urlValue || this.loadingAfter || this.afterExhausted) return;
    const newest = this.newestMessageElement();
    if (!newest) return;

    this.loadingAfter = true;

    try {
      const response = await fetch(this.buildUrl({ after: newest.dataset.messageId }), {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
      });

      if (!response.ok) return;

      const html = await response.text();
      if (!html.trim()) {
        this.afterExhausted = true;
        this.pendingRealtimeCount = 0;
        this.bottomObserver?.disconnect();
        return;
      }

      // A confirmed contiguous forward fill (fetched `after:` the loaded
      // window's own tail) closes whatever pending-realtime gap SF2's
      // fallback was tracking -- the fetch is a fresh DB read, so any
      // message that triggered the earlier discard is now either included
      // here or was itself superseded by it.
      this.pendingRealtimeCount = 0;
      this.insertAfter(html);
    } catch (error) {
      console.error("Error loading newer chat messages:", error);
    } finally {
      this.loadingAfter = false;
    }
  }

  // Realtime contiguity guard (finding #1 hardening): a Turbo Stream
  // message_created append lands directly on the feed via chat_controller's
  // own MutationObserver path -- it never goes through loadAfter/insertAfter,
  // so left unchecked it can desync `bottomObserver`/`afterExhausted` from
  // the true DOM-newest node, or splice a node in that isn't actually
  // contiguous with the loaded window.
  //
  // The DOM feed must always be a CONTIGUOUS window of messages. `afterExhausted`
  // (seeded synchronously in `connect()`, then kept current by `loadAfter`'s
  // own empty-page confirmation) is exactly that signal:
  //   - true: the loaded window already reaches the true newest message --
  //     the append is contiguous. NIT fix: `retargetObservers` itself skips
  //     re-observing `bottomObserver` while `afterExhausted` stays true
  //     (nothing further to load forward), so this call is a no-op for the
  //     bottom edge; it only exists in case `topObserver`/`beforeExhausted`
  //     bookkeeping ever needs re-establishing from this same entry point.
  //   - false: the loaded window is a backlog/anchor page NOT confirmed to
  //     reach the true newest. Two sub-cases, both handled below:
  //
  //     - SF1 (own message): an own send is, by definition, the true newest
  //       at send time -- the sender expects to land at the bottom seeing
  //       it, same as any chat UX. Neither keeping it (same gap risk as any
  //       other append) nor silently removing it with no follow-up (a
  //       regression: the sender's own message would just vanish with no
  //       path back) is acceptable. Delegate to `jumpToLatest()` instead --
  //       it wipes the feed (taking this premature node with it, so no
  //       separate `node.remove()` is needed) and refetches the true newest
  //       page, which already includes the just-sent message, landing the
  //       sender at the bottom. Fire-and-forget is safe: `jumpToLatest`
  //       already serializes against loadingBefore/loadingAfter itself.
  //
  //     - SF2 (incoming message): splicing it in could leave a permanent gap
  //       between the window's tail and it (see `loadAfter`: once
  //       `afterExhausted` flips true on an empty fetch, the observer
  //       disconnects for good and a gap would never get filled), so pull
  //       the node back out -- it will be loaded in correct order once the
  //       user scrolls down (`loadAfter` fills forward from the true tail)
  //       or jumps to latest (which refetches the newest page outright).
  //       chat_controller's badge/sound/unread side effects are triggered
  //       independently of this DOM node, so removing it here does not
  //       suppress them -- a real message did arrive. Known tradeoff: this
  //       window may in fact already reach the true newest (just not yet
  //       confirmed by an empty `loadAfter` page) -- message ids are not
  //       guaranteed gapless (deletes, shared sequences), so we can't
  //       cheaply tell a genuinely-contiguous arrival apart from a backlog
  //       one by id alone. Rather than leave an open-panel viewer with zero
  //       indication anything arrived, bump `pendingRealtimeCount` so
  //       chat_controller's jump-to-latest button still surfaces as a
  //       signal even though the discarded node left no DOM trace (see
  //       chat_controller.js#countMessagesBelowViewport).
  handleRealtimeAppend(node, { isOwn = false } = {}) {
    if (this.afterExhausted) {
      this.retargetObservers();
      return;
    }

    if (isOwn) {
      this.jumpToLatest();
      return;
    }

    node.remove();
    this.pendingRealtimeCount += 1;
  }

  // Jump-to-latest (delegated from chat_controller#scrollToLatest, D8 fix):
  // when the newest page is already the loaded window, a plain scroll
  // suffices. Otherwise the loaded window sits somewhere in the backlog (the
  // anchor case), so the true newest message isn't in the DOM at all -- fetch
  // the show endpoint with no cursor (newest 30, chronological ascending,
  // same shape `#show` already returns) and REPLACE the feed's messages with
  // it, same seed-before-insert discipline as loadBefore/loadAfter.
  async jumpToLatest() {
    if (this.afterExhausted) {
      this.element.scrollTop = this.element.scrollHeight;
      return;
    }

    if (!this.urlValue || this.loadingBefore || this.loadingAfter) return;

    // Serialize against reverse pagination too, not just another forward
    // load -- otherwise a top-intersection `loadBefore` that starts while
    // this fetch is in flight could resolve after the window replace below
    // and prepend an older page contiguous with the OLD (now-discarded)
    // anchor window, leaving a permanent gap above the freshly inserted
    // newest page.
    this.loadingAfter = true;
    this.loadingBefore = true;

    try {
      const response = await fetch(this.buildUrl({}), {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
      });

      if (!response.ok) return;

      const html = await response.text();
      const nodes = this.parseNodes(html);
      if (nodes.length === 0) return;

      this.seedSeenIds(nodes);

      this.element
        .querySelectorAll(".chat-message, .chat-day-divider, .chat-unread-divider")
        .forEach((node) => node.remove());

      const fragment = document.createDocumentFragment();
      nodes.forEach((node) => fragment.appendChild(node));
      this.element.appendChild(fragment);

      this.afterExhausted = true;
      this.beforeExhausted = false;
      // The freshly fetched newest page supersedes whatever SF2's
      // pending-realtime fallback was tracking -- the window is now
      // confirmed at the true newest, so there is nothing left pending.
      this.pendingRealtimeCount = 0;
      this.bottomObserver?.disconnect();
      this.retargetObservers();

      this.element.scrollTop = this.element.scrollHeight;
    } catch (error) {
      console.error("Error loading newest chat messages:", error);
    } finally {
      this.loadingAfter = false;
      this.loadingBefore = false;
    }
  }

  // --- Insertion (D6: prepend-with-scroll-preservation) ---

  insertBefore(html) {
    const nodes = this.parseNodes(html);
    if (nodes.length === 0) return;

    this.seedSeenIds(nodes);

    const oldScrollHeight = this.element.scrollHeight;
    const oldScrollTop = this.element.scrollTop;
    // Must be an Element (has getBoundingClientRect), not `this.element.firstChild`
    // -- the feed's real DOM firstChild is typically a whitespace Text node
    // (the newline right after the opening tag in the ERB), which has no
    // getBoundingClientRect and would throw. `oldestMessageElement()` is
    // called before insertion, so it's still the pre-insert oldest message
    // -- guaranteed to exist here since `loadBefore` already checked it.
    const boundaryNode = this.oldestMessageElement();

    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => fragment.appendChild(node));
    this.element.insertBefore(fragment, this.element.firstChild);

    const newScrollHeight = this.element.scrollHeight;
    this.element.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);

    // Async media reflow mitigation (G2 hardening): unsized <img>/<audio> in
    // the just-prepended nodes grow layout again once they finish loading,
    // after the correction above already ran. Anchored to this page's own
    // boundary node (the message that was the pre-insert oldest, now sitting
    // right after the freshly prepended block) via getBoundingClientRect()
    // rather than the container's `scrollHeight` -- `scrollHeight` is shared
    // mutable state that a second, concurrent prepend (an older page loading
    // while this page's media is still in flight) also mutates, which would
    // double-apply the second page's height onto this page's late
    // correction. A specific node's bounding rect stays correct regardless
    // of what else prepends around it.
    if (boundaryNode) this.watchMediaReflow(nodes, boundaryNode);

    this.retargetObservers();
  }

  insertAfter(html) {
    const nodes = this.parseNodes(html);
    if (nodes.length === 0) return;

    this.seedSeenIds(nodes);

    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => fragment.appendChild(node));
    this.element.appendChild(fragment);

    this.retargetObservers();
  }

  watchMediaReflow(nodes, boundaryNode) {
    let referenceTop = boundaryNode.getBoundingClientRect().top;

    nodes.forEach((node) => {
      node.querySelectorAll("img, audio").forEach((mediaEl) => {
        const eventName = mediaEl.tagName === "IMG" ? "load" : "loadedmetadata";
        mediaEl.addEventListener(
          eventName,
          () => {
            // If the boundary node was itself replaced/removed in the
            // meantime (e.g. an edit/delete broadcast), there is nothing
            // stable left to measure against -- skip rather than risk a
            // bogus correction from a detached node's zeroed rect.
            if (!boundaryNode.isConnected) return;

            const newTop = boundaryNode.getBoundingClientRect().top;
            const delta = newTop - referenceTop;
            if (delta !== 0) this.element.scrollTop += delta;
            referenceTop = newTop;
          },
          { once: true }
        );
      });
    });
  }

  // Seeds fetched ids into the chat controller's seen-set BEFORE the nodes
  // are inserted into the DOM (D7) -- must run strictly before
  // insertBefore/insertAfter mutate `this.element`, because chat_controller's
  // MutationObserver callback is delivered asynchronously (a microtask,
  // after the current insertion already ran) and reads `seenMessageIds` at
  // that later point -- seeding here just has to happen before the mutation
  // is queued, not before some synchronous read.
  //
  // `hasChatOutlet` is only false before `maybeStart()` has gated fetching on
  // it (see the class docstring), so by the time insertBefore/insertAfter
  // run it's expected to be true; this is a defense-in-depth no-op guard,
  // not a path the insert methods otherwise account for.
  seedSeenIds(nodes) {
    if (!this.hasChatOutlet) return;

    nodes.forEach((node) => {
      const id = node.dataset?.messageId;
      if (id) this.chatOutlet.seenMessageIds.add(String(id));
    });
  }

  parseNodes(html) {
    const container = document.createElement("div");
    container.innerHTML = html;
    return Array.from(container.children);
  }

  oldestMessageElement() {
    return this.element.querySelector(".chat-message[data-message-id]");
  }

  newestMessageElement() {
    const nodes = this.element.querySelectorAll(".chat-message[data-message-id]");
    return nodes.length ? nodes[nodes.length - 1] : null;
  }

  buildUrl(params) {
    const url = new URL(this.urlValue, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
    return url.toString();
  }
}
